import React, { useEffect, useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';
import {
    Button,
    Checkbox,
    Combobox,
    ComboboxData,
    ComboboxItem,
    Divider,
    Flex,
    Group,
    Input,
    InputBase,
    Loader,
    Modal,
    Notification,
    NumberInput,
    Paper,
    Radio,
    Select,
    SimpleGrid,
    Stack,
    Text,
    TextInput,
    Title,
    useCombobox,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import * as z from 'zod';
import { t } from 'i18next';
import { useParams } from 'react-router';
import {
    IconArrowLeft,
    IconCalendar,
    IconClipboardCheck,
    IconClipboardText,
    IconHospital,
    IconNumber,
    IconPlus,
    IconTestPipe,
    IconUser,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { CalendarDateInput } from '../../../global/component/molecules/CalendarDateInput';
import { AppMedError } from '../../../global/error/AppMedError.ts';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import { useGetNewTask } from '../../../global/entities/Tasks/queries/useGetNewTask.tsx';
import { ITask, ITaskPayload } from '../../../global/entities/Tasks/Tasks.ts';
import { useDisclosure } from '@mantine/hooks';
import { usePostPartialCollaborator } from '../../../global/entities/Collaborator/mutations/usePostPartialCollaboratorMutations.tsx';
import { useGetPatientCollaboratorSummaries } from '../../../global/entities/Collaborator/queries/useGetPatientCollaboratorSummaries.tsx';
import { Collaborator, ICollaboratorcomboboxItem } from '../../../global/entities/Collaborator/Collaborator.ts';
import { usePostTaskMutations } from '../../../global/entities/Tasks/mutations/usePostTaskMutations.tsx';
import { useGetTasks } from '../../../global/entities/Tasks/queries/useGetTasks.tsx';
import { useGetTaskById } from '../../../global/entities/Tasks/queries/useGetTaskById.tsx';
import { useDeleteTaskMutations } from '../../../global/entities/Tasks/mutations/useDeleteTaskMutations.tsx';
import { useGetTestKits } from '../../../global/entities/Tasks/queries/useGetTestKits.tsx';
import { TaskType } from '../../../global/types';
import { theme } from '../../../global/style/theme.tsx';
import { RouteStore } from '../../../global/Store/RouteStore.ts';
import { PatientStore } from '../../../global/Store/PatientStore.ts';
import { useGetTasksByPatient } from '../../../global/entities/Tasks/queries/useGetTasksByPatient.tsx';
import { useGetQuestionnaireIdsByFacilityId } from '../../../global/entities/healthcareFacility/queries/useGetQuestionnaireIdsByFacilityId.tsx';

export type ITaskFormProps = {
    routeStore?: RouteStore;
    patientStore?: PatientStore;
};

const TaskForm: React.FC<ITaskFormProps> = inject('routeStore', 'patientStore')(
    observer(({ routeStore, patientStore }) => {
        const schema = z.object({
            patientID: z.number().min(1, { message: t('formError.patientRequired') }),
            healthcareFacilityID: z.number().min(1, { message: t('formError.facilityRequired') }),
            taskTypeID: z.number().min(1, { message: t('formError.taskTypeRequired') }),
            availableDate: z.preprocess(
                (value) => {
                    if (typeof value === 'string' && value.trim() === '') return null;
                    return value ? new Date(value.toString()) : null;
                },
                z
                    .date({
                        required_error: t('formError.dateIsRequired'),
                        invalid_type_error: t('formError.dateIsRequired'),
                    })
                    .refine(
                        (date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const d = new Date(date);
                            d.setHours(0, 0, 0, 0);
                            return d >= today;
                        },
                        { message: t('formError.dueDateMustBeFuture') },
                    )
                    .refine(
                        (date) => {
                            if (!date || !form.values?.dueDate) return true;
                            const avail = new Date(date);
                            avail.setHours(0, 0, 0, 0);
                            const due = new Date(form.values.dueDate);
                            due.setHours(0, 0, 0, 0);
                            return avail <= due;
                        },
                        { message: t('formError.availableDateMustBeBeforeDueDate', 'Available date must be on or before the due date') },
                    ),
            ),
            dueDate: z.preprocess(
                (value) => {
                    if (typeof value === 'string' && value.trim() === '') return null;
                    return value ? new Date(value.toString()) : null;
                },
                z
                    .date({
                        required_error: t('formError.dueDateRequired'),
                        invalid_type_error: t('formError.dateIsRequired'),
                    })
                    .refine(
                        (date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const d = new Date(date);
                            d.setHours(0, 0, 0, 0);
                            return d >= today;
                        },
                        { message: t('formError.dueDateMustBeFuture') },
                    ),
            ),
            completedBy: z.string().optional().default(''),
            questionnaireID: z
                .number()
                .refine((value) => !(form.values.taskTypeID === TaskType.questionnaire && value === 0), {
                    message: t('formError.questionnaireRequired'),
                }),
            collaboratorID: z
                .number()
                .refine(
                    (value) =>
                        !(
                            form.values.taskTypeID === TaskType.questionnaire &&
                            value === 0 &&
                            completedBy === 'collaborator'
                        ),
                    { message: t('formError.collaboratorRequired') },
                ),
            biomarkerSpecimenID: z
                .number()
                .refine((value) => !(form.values.taskTypeID === TaskType.tdm && value === 0), {
                    message: t('formError.biomarkerRequired'),
                })
                .refine((value) => !(form.values.taskTypeID === TaskType.externalBiomarker && value === 0), {
                    message: t('formError.biomarkerRequired'),
                }),
            testKitOrderLineID: z
                .string()
                .nullable()
                .refine((value) => !(form.values.taskTypeID === TaskType.tdm && !value), {
                    message: t('formError.sampleNumber'),
                }),
            isRecurring: z.boolean().optional(),
            recurrenceType: z.string().optional(),
            recurrenceInterval: z.number().optional(),
            recurrenceEndDate: z.preprocess(
                (value) => {
                    if (typeof value === 'string' && value.trim() === '') return null;
                    return value ? new Date(value.toString()) : null;
                },
                z
                    .date({
                        required_error: t('formError.recurrenceEndDateRequired'),
                        invalid_type_error: t('formError.dateIsRequired'),
                    })
                    .refine(
                        (date) => {
                            // Only validate if isRecurring is true
                            if (!form.values?.isRecurring) return true;
                            return date !== null;
                        },
                        { message: t('formError.recurrenceEndDateRequired') },
                    )
                    .refine(
                        (date) => {
                            if (!form.values?.isRecurring || !date || !form.values?.dueDate) return true;
                            const end = new Date(date);
                            end.setHours(0, 0, 0, 0);
                            const due = new Date(form.values.dueDate);
                            due.setHours(0, 0, 0, 0);
                            return end > due;
                        },
                        { message: t('formError.recurrenceEndDateMustBeAfterDueDate') },
                    )
                    .nullable()
            ),
            templateName: z.string().optional(),
            expirationDays: z.preprocess(
                (value) => (value === '' || value === null ? undefined : value),
                z
                    .number()
                    .int()
                    .positive({ message: t('formError.expirationDaysMustBePositive') })
                    .optional(),
            ),
        });

        const form: any = useForm<ITaskPayload>({
            mode: 'controlled',
            initialValues: {
                patientID: 0,
                healthcareFacilityID: 0,
                availableDate: null,
                dueDate: null,
                taskTypeID: 0,
                questionnaireID: 0,
                collaboratorID: 0,
                biomarkerSpecimenID: 0,
                testKitOrderLineID: null,
                isRecurring: false,
                recurrenceType: 'DAILY_AM',
                recurrenceInterval: 1,
                recurrenceEndDate: null,
                templateName: '',
                expirationDays: undefined,
                sendNotificationOnCreate: false,
            },
            validate: zodResolver(schema),
        });

        // 🧭 ROUTING & NAVIGATION
        const { id } = useParams<{ id: string }>();
        const navigate = useNavigate();

        // 🧠 FORM & LOCAL STATE
        const [isSubmitting, setIsSubmitting] = useState(false); // Submission in progress
        const [error, setError] = useState<string | null>(null); // Generic error
        const [biomarkerError, setBiomarkerError] = useState(''); // Specific error related to biomarkers
        const [newRole, setNewRole] = useState(''); // New collaborator role
        const [completedBy, setCompletedBy] = useState<string>('patient'); // Who completes the task
        const [testKitOrderLineID, setTestKitOrderLineID] = useState(''); // Selected test kit ID
        const [selectedHealthcareFacilityId, setSelectedHealthcareFacilityId] = useState<number>(0); // Selected facility for questionnaire filtering

        // 📡 API - QUERIES & MUTATIONS
        const { data: task } = useGetTaskById(); // Fetch task by ID
        const { data: newTaskData, isLoading: isLoadingNewTaskData, refetch: refetchNewTaskData } = useGetNewTask(); // Get default values for new task
        const { data: collaboratorsData, refetch: refetchCollaboratorsData } = useGetPatientCollaboratorSummaries(
            form.values.patientID,
        ); // List of collaborators
        const { refetch: refetchTasks } = useGetTasks(); // Refetch task list
        const { refetch: refetchTasksPatient } = useGetTasksByPatient(); // Refetch task list
        const { refetch: refetchTestKit, isLoading: isLoadingTestKit } = useGetTestKits(testKitOrderLineID, false); // Test kits for task

        const postPartialCollaborator = usePostPartialCollaborator(); // Create partial collaborator
        const postTask = usePostTaskMutations(); // Create/update task
        const deleteTask = useDeleteTaskMutations(); // Delete task
        
        // 🎯 QUESTIONNAIRE FILTERING
        const { data: facilityQuestionnaireIds } = useGetQuestionnaireIdsByFacilityId(
            selectedHealthcareFacilityId,
            selectedHealthcareFacilityId > 0
        ); // Get questionnaire IDs for selected facility

        // 📦 MODAL STATE
        const [opened, { open, close }] = useDisclosure(false); // Modal state for collaborator
        const readOnly = !!id && id !== '0';

        const combobox = useCombobox({
            onDropdownClose: () => combobox.resetSelectedOption(),
        });

        const handleCancel = () => {
            navigate(routeStore?.lastRoute || '..');
        };

        const comboboxData: {
            patients: ComboboxData;
            healthcareFacilities: ComboboxData;
            taskTypes: ComboboxData;
            questionnaires: ComboboxData;
            biomarkerSpecimens: ComboboxData;
            collaborators: ComboboxData;
        } = useMemo(() => {
            // Choose biomarker list based on task type:
            // TaskTypeID=2 (TaskType.tdm) uses internal biomarkers
            // TaskTypeID=3 (TaskType.biomarkerExt) uses external biomarkers
            const selectedBiomarkers = form.values.taskTypeID === TaskType.externalBiomarker 
                ? newTaskData?.externalBiomarkersCombobox ?? []
                : newTaskData?.biomarkersCombobox ?? [];

            // Filter questionnaires based on selected healthcare facility
            let filteredQuestionnaires = newTaskData?.questionnairesCombobox ?? [];
            if (selectedHealthcareFacilityId > 0 && facilityQuestionnaireIds) {
                filteredQuestionnaires = filteredQuestionnaires.filter(questionnaire => 
                    facilityQuestionnaireIds.includes(Number(questionnaire.value))
                );
            }
                
            return {
                patients: newTaskData?.patientsCombobox ?? [],
                healthcareFacilities: newTaskData?.healthcareFacilitiesCombobox ?? [],
                taskTypes: newTaskData?.taskTypesCombobox ?? [],
                questionnaires: filteredQuestionnaires,
                biomarkerSpecimens: selectedBiomarkers,
                collaborators: newTaskData?.collaboratorsCombobox ?? [],
            };
        }, [newTaskData, form.values.taskTypeID, selectedHealthcareFacilityId, facilityQuestionnaireIds]);

        // 13.3 Auto-select facility if HCP has only one, 13.4 Default dates to today, 13.5 Pre-fill patient from context
        useEffect(() => {
            if (!newTaskData || (id && id !== '0')) return; // Only for new task creation

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // 13.4: Default dates to today
            if (!form.values.availableDate)
                form.setFieldValue('availableDate', today);
            if (!form.values.dueDate)
                form.setFieldValue('dueDate', today);

            // 13.3: Auto-select facility if only one
            if (newTaskData.healthcareFacilities?.length === 1 && form.values.healthcareFacilityID === 0) {
                const facilityId = newTaskData.healthcareFacilities[0].id;
                form.setFieldValue('healthcareFacilityID', facilityId);
                setSelectedHealthcareFacilityId(facilityId);
            }

            // 13.5: Pre-fill patient from patient context (when navigating from a patient view)
            const isInPatientContext = location.pathname.includes('hcpPatientDashboard');
            if (isInPatientContext && patientStore?.patientId && form.values.patientID === 0) {
                const pid = parseInt(patientStore.patientId);
                if (pid > 0)
                    form.setFieldValue('patientID', pid);
            }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [newTaskData, id]);

        useEffect(() => {
            if (form.values.testKitOrderLineID) {
                setBiomarkerError('');
                setTestKitOrderLineID(form.values.testKitOrderLineID);
            }
        }, [form.values.testKitOrderLineID]);

        useEffect(() => {
            if (form.values.patientID !== 0) 
                refetchCollaboratorsData();
            
        }, [form.values.patientID]);

        // 🎯 Initialize selected facility for questionnaire filtering
        useEffect(() => {
            if (form.values.healthcareFacilityID && form.values.healthcareFacilityID !== selectedHealthcareFacilityId) 
                setSelectedHealthcareFacilityId(form.values.healthcareFacilityID);
            
        }, [form.values.healthcareFacilityID]);

        const handleSubmit = async (formValues: ITaskPayload) => {
            setError(null);
            setIsSubmitting(true);
            
            // Only validate test kit for internal biomarker tasks (TaskType.tdm), not external tasks
            if (formValues.taskTypeID === TaskType.tdm) {
                const rep = await refetchTestKit();
                
                if (
                    form.values.testKitOrderLineID &&
                    rep.data?.sampleNumber !== form.values.testKitOrderLineID
                ) {
                    form.values.testKitOrderLineID = null;
                    setBiomarkerError(t('formError.invalidBiomarker'));
                    setIsSubmitting(false);
                    return;
                }
                
                try {
                    if (id) {
                        await deleteTask.mutateAsync(id);
                        await postTask.mutateAsync({
                            patientID: formValues.patientID,
                            healthcareFacilityID: formValues.healthcareFacilityID,
                            taskTypeID: formValues.taskTypeID,
                            availableDate: formValues.availableDate,
                            dueDate: formValues.dueDate,
                            questionnaireID: (formValues.taskTypeID as number) != 2 && (formValues.taskTypeID as number) != 3 ? formValues.questionnaireID : null,
                            collaboratorID:
                                (formValues.taskTypeID as number) != 2 && (formValues.taskTypeID as number) != 3 && completedBy === 'collaborator'
                                    ? formValues.collaboratorID
                                    : null,
                            biomarkerSpecimenID: (formValues.taskTypeID as number) != 1 ? formValues.biomarkerSpecimenID : null,
                            testKitOrderLineID: formValues.taskTypeID === TaskType.tdm ? rep.data?.testKitOrderLineID : null,
                            isRecurring: (formValues.taskTypeID as number) != 2 && (formValues.taskTypeID as number) != 3 && completedBy !== 'collaborator' 
                                ? formValues.isRecurring : false,
                            recurrenceType: formValues.recurrenceType,
                            recurrenceInterval: formValues.recurrenceInterval,
                            recurrenceEndDate: formValues.recurrenceEndDate,
                            expirationDays: formValues.expirationDays,
                            sendNotificationOnCreate: formValues.sendNotificationOnCreate,
                        } as ITaskPayload as ITask);
                        
                        handleCancel();
                    } else 
                        console.error('taskId is undefined');
                    
                } catch (e) {
                    if (e instanceof AppMedError) 
                        setError(e.message);
                     else 
                        setError('An unexpected error occurred.');
                    
                } finally {
                    await refetchTasks();
                    await refetchTasksPatient();
                    setIsSubmitting(false);
                }
            } else {
                // For external tasks and questionnaires - no test kit validation needed
                try {
                    if (id) {
                        await deleteTask.mutateAsync(id);
                        await postTask.mutateAsync({
                            patientID: formValues.patientID,
                            healthcareFacilityID: formValues.healthcareFacilityID,
                            taskTypeID: formValues.taskTypeID,
                            availableDate: formValues.availableDate,
                            dueDate: formValues.dueDate,
                            questionnaireID: (formValues.taskTypeID as number) != 2 && (formValues.taskTypeID as number) != 3 ? formValues.questionnaireID : null,
                            collaboratorID:
                                (formValues.taskTypeID as number) != 2 && (formValues.taskTypeID as number) != 3 && completedBy === 'collaborator'
                                    ? formValues.collaboratorID
                                    : null,
                            biomarkerSpecimenID: (formValues.taskTypeID as number) != 1 ? formValues.biomarkerSpecimenID : null,
                            testKitOrderLineID: null, // External tasks don't use test kit order line IDs
                            isRecurring: (formValues.taskTypeID as number) != 2 && (formValues.taskTypeID as number) != 3 && completedBy !== 'collaborator' 
                                ? formValues.isRecurring : false,
                            recurrenceType: formValues.recurrenceType,
                            recurrenceInterval: formValues.recurrenceInterval,
                            recurrenceEndDate: formValues.recurrenceEndDate,
                            expirationDays: formValues.expirationDays,
                            sendNotificationOnCreate: formValues.sendNotificationOnCreate,
                        } as ITaskPayload as ITask);
                        
                        handleCancel();
                    } else 
                        console.error('taskId is undefined');
                    
                } catch (e) {
                    if (e instanceof AppMedError) 
                        setError(e.message);
                     else 
                        setError('An unexpected error occurred.');
                    
                } finally {
                    await refetchTasks();
                    await refetchTasksPatient();
                    setIsSubmitting(false);
                }
            }
        };

        useEffect(() => {
            if (task && id && task.id !== form.values.id) {
                form.reset();
                if (task.collaboratorID) 
                    setCompletedBy('collaborator');
                 else 
                    setCompletedBy('patient');
                
                form.setValues({
                    patientID: task.patientID,
                    healthcareFacilityID: task.healthcareFacilityID,
                    dateOrdered: task.dateOrdered ? new Date(task.dateOrdered) : undefined,
                    availableDate: task.availableDate ? new Date(task.availableDate) : undefined,
                    dueDate: task.dueDate ? new Date(task.dueDate) : undefined,
                    taskTypeID: task.taskTypeID,
                    questionnaireID: task.questionnaireID || 0,
                    collaboratorID: task.collaboratorID || 0,
                    biomarkerSpecimenID: task.biomarkerSpecimenID || 0,
                    testKitOrderLineID: task.testKitNumber || 0,
                    isRecurring: task.isRecurring || false,
                    recurrenceType: task.recurrenceType || 'DAILY_AM',
                    recurrenceInterval: task.recurrenceInterval || 1,
                    recurrenceEndDate: task.recurrenceEndDate ? new Date(task.recurrenceEndDate) : undefined,
                    templateName: task.templateName || '',
                    expirationDays: task.expirationDays,
                });
            } else if (!id) 
                form.reset();
            
        }, [id, task]);

        const collaboratorsComboboxInput: React.JSX.Element = useMemo(() => {
            return (
                <InputBase
                    component="button"
                    type="button"
                    pointer
                    rightSection={<Combobox.Chevron />}
                    onClick={() => combobox.toggleDropdown()}
                    rightSectionPointerEvents="none"
                    styles={{ input: readOnly ? { backgroundColor: '#f1f3f5', cursor: 'not-allowed' } : {} }}
                >
                    {form.values.collaboratorID ? (
                        <Flex gap={'sm'}>
                            <Text c={readOnly ? 'dimmed' : 'black'}>
                                {
                                    collaboratorsData?.find(
                                        (collabo: Collaborator) => collabo.id == form.values.collaboratorID?.toString(),
                                    )?.role
                                }
                            </Text>
                            <Text c="dimmed">
                                {
                                    collaboratorsData?.find(
                                        (collabo: Collaborator) => collabo.id == form.values.collaboratorID?.toString(),
                                    )?.name
                                }
                            </Text>
                        </Flex>
                    ) : (
                        <Input.Placeholder>{t('formTask.selectCollaboratorPlaceholder')}</Input.Placeholder>
                    )}
                </InputBase>
            );
        }, [form.values.collaboratorID, collaboratorsData]);

        const collaboratorsComboboxItems: any[] | React.JSX.Element[] = useMemo(() => {
            if (!collaboratorsData) 
                return [];
            
            return [
                {
                    groupLabel: t('formTask.newCollaborator'),
                    items: [
                        {
                            value: 0,
                            name: t('formTask.addCollaborator'),
                        },
                    ],
                },
                {
                    groupLabel: t('formTask.selectCollaborator'),
                    items: collaboratorsData?.map((colabo: Collaborator) => colabo.comboboxItem),
                },
            ].map((group, index) => (
                <Combobox.Options key={index}>
                    <Combobox.Group label={group.groupLabel}>
                        {group.items.map((item: ICollaboratorcomboboxItem) => {
                            return (
                                <Combobox.Option key={item.value} value={item.value.toString()}>
                                    {item.value == 0 ? (
                                        <Flex w={'100%'} align={'center'} gap={'xs'} onClick={() => open()}>
                                            <IconPlus color="var(--mantine-color-blue-text)" />
                                            <Text size="sm" fw={600} c="blue">
                                                {item.name}
                                            </Text>
                                        </Flex>
                                    ) : (
                                        <Flex gap={'sm'}>
                                            <Text>{item.role}</Text>
                                            <Text c="dimmed">{item.name}</Text>
                                        </Flex>
                                    )}
                                </Combobox.Option>
                            );
                        })}
                    </Combobox.Group>
                </Combobox.Options>
            ));
        }, [collaboratorsData]);

        const saveRole = async () => {
            if (form.values.patientID) {
                await postPartialCollaborator
                    .mutateAsync({
                        patientID: parseInt(form.values.patientID.toString()),
                        role: newRole,
                    })
                    .then((collaboratorId: number) => {
                        refetchCollaboratorsData();
                        refetchNewTaskData().then(() => form.setValues({ collaboratorID: collaboratorId }));
                    });
            }
            close();
        };

        useEffect(() => {
            if (form.values.testKitOrderLineID) 
                setTestKitOrderLineID(form.values.testKitOrderLineID);
            
        }, [form.values.testKitOrderLineID]);

        // Uncheck sendNotificationOnCreate when availableDate is in the future or completedBy is collaborator
        useEffect(() => {
            const isAvailableDateInFuture = form.values.availableDate && (() => {
                const availableDateStart = new Date(form.values.availableDate);
                availableDateStart.setHours(0, 0, 0, 0);
                const todayStart = new Date();
                todayStart.setHours(0, 0, 0, 0);
                return availableDateStart > todayStart;
            })();
            
            if ((isAvailableDateInFuture || completedBy === 'collaborator') && form.values.sendNotificationOnCreate) 
                form.setFieldValue('sendNotificationOnCreate', false);
            
        }, [form.values.availableDate, completedBy]);

        if (isLoadingNewTaskData) 
            return <Loading visible={true} loadingMessage={'Loading...'} />;

        return (
            <Stack gap={'lg'} pt={location.pathname.includes('hcpPatientDashboard') ? 'none' : 'xl'}>
                <Flex
                    align={'center'}
                    justify={'space-between'}
                    hidden={location.pathname.includes('hcpPatientDashboard')}
                >
                    <Button
                        leftSection={<IconArrowLeft color={theme.colors?.blue?.toString()} />}
                        onClick={() => navigate('/hcpDashboard/task')}
                        variant="transparent"
                    >
                        <Title c="black" order={3}>
                            {task?.id ? `${t('page.task.task')} ${task.id}` : t('page.task.addTask')}
                        </Title>
                    </Button>
                </Flex>
                <Paper>
                    <form onSubmit={form.onSubmit((values: any) => handleSubmit(values))}>
                        <Stack>
                            <SimpleGrid cols={{ base: 1, sm: 2 }}>
                                <Select
                                    disabled={readOnly || location.pathname.includes('hcpPatientDashboard')}
                                    searchable
                                    label={t('formTask.selectPatient')}
                                    placeholder={t('formTask.selectPatient')}
                                    leftSection={<IconUser size={16} />}
                                    data={comboboxData?.patients}
                                    onChange={(value) => form.setFieldValue('patientID', Number(value))}
                                    value={form.values.patientID > 0 ? form.values.patientID.toString() : null}
                                    error={form.getInputProps('patientID').error}
                                />
                                <Select
                                    disabled={readOnly || (newTaskData?.healthcareFacilities?.length === 1 && (!id || id === '0'))}
                                    label={t('hcf.title')}
                                    placeholder={t('hcf.select')}
                                    leftSection={<IconHospital size={16} />}
                                    data={comboboxData?.healthcareFacilities}
                                    onChange={(value) => {
                                        const facilityId = Number(value);
                                        form.setFieldValue('healthcareFacilityID', facilityId);
                                        setSelectedHealthcareFacilityId(facilityId);
                                        // Reset questionnaire selection when facility changes
                                        if (form.values.questionnaireID !== 0)
                                            form.setFieldValue('questionnaireID', 0);

                                    }}
                                    value={
                                        form.values.healthcareFacilityID > 0
                                            ? form.values.healthcareFacilityID.toString()
                                            : null
                                    }
                                    error={form.getInputProps('healthcareFacilityID').error}
                                />
                            </SimpleGrid>
                            {form.values.patientID ? (
                                <SimpleGrid cols={{ base: 2, sm: 4 }}>
                                    <CalendarDateInput
                                        label={t('formTask.dateOrdered')}
                                        placeholder={t('placeHolder.dateFormat')}
                                        leftSection={<IconCalendar size={16} />}
                                        value={new Date()}
                                        disabled
                                    />
                                    <CalendarDateInput
                                        disabled={readOnly}
                                        label={t('formTask.availableDate', 'Available date')}
                                        placeholder={t('placeHolder.dateFormat')}
                                        leftSection={<IconCalendar size={16} />}
                                        valueFormat="YYYY-MM-DD"
                                        {...form.getInputProps('availableDate')}
                                        minDate={new Date()}
                                    />
                                    <CalendarDateInput
                                        disabled={readOnly}
                                        label={t('formTask.dueDate')}
                                        placeholder={t('placeHolder.dateFormat')}
                                        leftSection={<IconCalendar size={16} />}
                                        valueFormat="YYYY-MM-DD"
                                        {...form.getInputProps('dueDate')}
                                        minDate={new Date()}
                                    />
                                    <Select
                                        disabled={readOnly}
                                        label={t('formTask.taskType')}
                                        placeholder={t('formTask.taskTypePlaceHolder')}
                                        leftSection={<IconClipboardCheck size={16} />}
                                        data={comboboxData?.taskTypes}
                                        onChange={(value) => form.setFieldValue('taskTypeID', Number(value))}
                                        value={form.values.taskTypeID > 0 ? form.values.taskTypeID.toString() : null}
                                        error={form.getInputProps('taskTypeID').error}
                                    />
                                </SimpleGrid>) : null}
                                
                            <SimpleGrid cols={{ base: 1, sm: 2 }}>
                                {form.values.taskTypeID?.toString() === '1' && (
                                    <Select
                                        disabled={readOnly}
                                        label={t('formTask.selectQuestionnaire')}
                                        placeholder={t('formTask.selectQuestionnaire')}
                                        leftSection={<IconClipboardText size={16} />}
                                        data={comboboxData?.questionnaires}
                                        onChange={(value) => form.setFieldValue('questionnaireID', Number(value))}
                                        value={form.values.questionnaireID?.toString()}
                                        error={form.getInputProps('questionnaireID').error}
                                    />
                                )}
                                {form.values.taskTypeID === 2 && (
                                    <Select
                                        disabled={readOnly}
                                        label={t('formTask.selectBiomarker')}
                                        placeholder={t('formTask.selectBiomarker')}
                                        leftSection={<IconTestPipe size={16} />}
                                        data={comboboxData?.biomarkerSpecimens}
                                        onChange={(value) =>
                                            form.setFieldValue('biomarkerSpecimenID', value ? Number(value) : null)
                                        }
                                        value={
                                            form.values.biomarkerSpecimenID
                                                ? form.values.biomarkerSpecimenID.toString()
                                                : null
                                        }
                                        error={form.getInputProps('biomarkerSpecimenID').error}
                                    />
                                )}
                                {form.values.taskTypeID === TaskType.externalBiomarker && (
                                    <Select
                                        disabled={readOnly}
                                        label={t('formTask.selectBiomarker')}
                                        placeholder={t('formTask.selectBiomarker')}
                                        leftSection={<IconTestPipe size={16} />}
                                        data={comboboxData?.biomarkerSpecimens}
                                        onChange={(value) =>
                                            form.setFieldValue('biomarkerSpecimenID', value ? Number(value) : null)
                                        }
                                        value={
                                            form.values.biomarkerSpecimenID
                                                ? form.values.biomarkerSpecimenID.toString()
                                                : null
                                        }
                                        error={form.getInputProps('biomarkerSpecimenID').error}
                                    />
                                )}
                                {form.values.taskTypeID === 2 && (
                                    <TextInput
                                        disabled={readOnly}
                                        label={t('modal.testKitModal.sampleNumber')}
                                        placeholder={t('modal.testKitModal.sampleNumber')}
                                        {...form.getInputProps('testKitOrderLineID')}
                                        value={form.values.testKitOrderLineID || ''}
                                        error={[form.getInputProps('testKitOrderLineID').error, biomarkerError]
                                            .filter(Boolean)
                                            .join(' | ')}
                                        leftSection={isLoadingTestKit ? <Loader size="sm" /> : <IconNumber />}
                                    />
                                )}
                            </SimpleGrid>
                            {form.values.taskTypeID?.toString() === '1' && form.getInputProps('questionnaireId') && (
                                <Group grow>
                                    <Radio.Group
                                        label={t('formTask.completedBy')}
                                        value={completedBy}
                                        onChange={(el) => {
                                            setCompletedBy(el);
                                        }}
                                        mt={'sm'}
                                    >
                                        <Flex gap={'md'}>
                                            <Radio
                                                disabled={readOnly}
                                                value="patient"
                                                label={
                                                    (
                                                        comboboxData?.patients.find(
                                                            (patient: any) => patient.value === form.values.patientID,
                                                        ) as ComboboxItem
                                                    )?.label || t('formTask.actualPatient')
                                                }
                                                size={'sm'}
                                                styles={{ label: { fontSize: 16 } }}
                                            />
                                            <Radio
                                                disabled={readOnly}
                                                value="collaborator"
                                                label={t('formTask.collaborator')}
                                                styles={{ label: { fontSize: 16 } }}
                                            />
                                        </Flex>
                                    </Radio.Group>
                                </Group>
                            )}
                            {completedBy === 'collaborator' && (
                                <Group grow>
                                    {form.values.taskTypeID == 1 && (
                                        <Input.Wrapper error={form.getInputProps('collaboratorID').error}>
                                            <Combobox
                                                disabled={readOnly}
                                                store={combobox}
                                                withinPortal={false}
                                                onOptionSubmit={(val) => {
                                                    form.setValues({ collaboratorID: parseInt(val) });
                                                    combobox.closeDropdown();
                                                }}
                                            >
                                                <Combobox.Target>{collaboratorsComboboxInput}</Combobox.Target>

                                                <Combobox.Dropdown>{collaboratorsComboboxItems}</Combobox.Dropdown>
                                            </Combobox>
                                        </Input.Wrapper>
                                    )}
                                </Group>
                            )}
                            <Modal opened={opened} onClose={close} title={t('formTask.collaborator')}>
                                <Text>{t('formTask.newCollaboratorModalTitle')}</Text>
                                <TextInput
                                    value={newRole}
                                    onChange={(e) => setNewRole(e.target.value)}
                                    placeholder={t('formTask.newCollaboratorModalEnterRole')}
                                />
                                <Flex mt="md" w={'100%'} justify={'flex-end'} gap={'md'}>
                                    <Button variant="default" color={'gray'} onClick={close}>
                                        {t('button.cancel')}
                                    </Button>
                                    <Button onClick={saveRole}>{t('button.save')}</Button>
                                </Flex>
                            </Modal>

                            {form.values.taskTypeID?.toString() === '1' && (!readOnly || (readOnly && form.values.isRecurring)) && completedBy !== 'collaborator' && (
                                <>
                                    <Divider />     
                                    <Group grow>
                                        <Checkbox
                                            disabled={readOnly}
                                            label={t('formTask.makeRecurring', 'Make this a recurring task')}
                                            {...form.getInputProps('isRecurring', { type: 'checkbox' })}
                                        />
                                    </Group>
                                </>
                            )}
                            
                            {form.values.isRecurring && form.values.taskTypeID?.toString() === '1'  && completedBy !== 'collaborator' && (
                                <Group grow>                                                                                                                                                        
                                <Paper p="md" withBorder style={{ backgroundColor: '#f8f9fa' }}>
                                    <Title order={5} mb="md">{t('formTask.recurrenceSettings', 'Recurrence Settings')}</Title>
                                    
                                      <Group>
                                        <Select
                                            disabled={readOnly}
                                            label={t('formTask.frequency', 'Frequency')}
                                            data={[
                                                { value: 'DAILY_AM', label: t('formTask.dailyAm', 'Daily AM') },
                                                { value: 'DAILY_PM', label: t('formTask.dailyPm', 'Daily PM') },
                                                { value: 'DAILY_NIGHT', label: t('formTask.dailyNight', 'Daily Night') },
                                                { value: 'WEEKLY', label: t('formTask.weekly', 'Weekly') },
                                                { value: 'MONTHLY', label: t('formTask.monthly', 'Monthly') }
                                            ]}
                                            {...form.getInputProps('recurrenceType')}
                                        />
                                        
                                        {form.values.recurrenceType === 'WEEKLY' && (
                                            <NumberInput
                                                disabled={readOnly}
                                                label={t('formTask.everyNWeeks', 'Every N weeks')}
                                                min={1}
                                                max={12}                    
                                                {...form.getInputProps('recurrenceInterval')}
                                            />
                                        )}
                                    </Group>
                                    
                                     <Group mt="md">
                                         <CalendarDateInput
                                             disabled={readOnly}
                                             label={t('formTask.endDate', 'End Date')}
                                             placeholder={t('placeHolder.dateFormat')}
                                             leftSection={<IconCalendar size={16} />}
                                             valueFormat="YYYY-MM-DD"
                                             required
                                             withAsterisk
                                             {...form.getInputProps('recurrenceEndDate')}
                                             minDate={form.values.dueDate ? new Date(form.values.dueDate) : new Date()}
                                         />
                                         <NumberInput
                                             disabled={readOnly}
                                             label={t('formTask.expirationDays', 'Expire after (days)')}
                                             {...form.getInputProps('expirationDays')}
                                         />
                                     </Group>
                                </Paper>
                                </Group>
                            )}

                            <Flex mt="md" justify={'flex-end'} gap={'md'} align={'center'} wrap="wrap">
                                {!readOnly && form.values.patientID > 0 && (
                                    <Checkbox
                                        label={t('formTask.sendNotificationOnCreate', 'Send notification to patient')}
                                        checked={
                                            (form.values.availableDate && new Date(form.values.availableDate).setHours(0,0,0,0) > new Date().setHours(0,0,0,0)) || completedBy === 'collaborator'
                                                ? false
                                                : form.values.sendNotificationOnCreate
                                        }
                                        onChange={(event) => form.setFieldValue('sendNotificationOnCreate', event.currentTarget.checked)}
                                        disabled={
                                            (form.values.availableDate && new Date(form.values.availableDate).setHours(0,0,0,0) > new Date().setHours(0,0,0,0)) || completedBy === 'collaborator'
                                        }
                                        style={{ flex: '1 1 auto' }}
                                    />
                                )}
                                <Flex gap="md">
                                    {!id || id === '0' ? (
                                        <Button variant="default" onClick={handleCancel}>
                                            {t('button.cancel')}
                                        </Button>
                                    ) : (
                                        <Button onClick={handleCancel}>
                                            {t('button.ok')}
                                        </Button>
                                    )}
                                    {(!id || id === '0') && (
                                        <Button loading={isSubmitting} type="submit">
                                            {t('button.save')}
                                        </Button>
                                    )}
                                </Flex>
                            </Flex>
                        </Stack>
                    </form>
                    {error && (
                        <Stack pt={'lg'}>
                            <Notification color="red" onClose={() => setError(null)}>
                                {error}
                            </Notification>
                        </Stack>
                    )}
                </Paper>
            </Stack>
        );
    }),
);

export default TaskForm;
